// UserSearch.js (logic-only; без JSX — рендер дадим следующим шагом)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
// Путь поправь под свой проект: если оставил в bookAPI — верни как было
import {fetchAssignmentsAdmin, fetchUsersAdmin} from "../../http/bookAPI";
import EditUserAdmin from "./EditUserAdmin";
import {Card, Table, Badge, Dropdown, Button, Form, Row, Col, Pagination, Spinner} from "react-bootstrap";
import {differenceInMinutes, format} from "date-fns";
import {enUS} from "date-fns/locale";
import AdminNegativeBalanceReconcile from "./AdminNegativeBalanceReconcile";
import AdminEmailMailerModal from "../adminEmailMailer/AdminEmailMailerModal";

const UserSearch = ({
    onOpenUserBooks,
    onOpenUserAuthorAssignments,
    onOpenUserReviewerAssignments,
}) => {
    // ===== Фильтры =====
    const [q, setQ] = useState("");                 // поиск по username/email/amz_name/audible_name/id
    const [tariffPlan, setTariffPlan] = useState(""); // FREE/ESSENTIAL/PREMIUM/MAXIMUM/SUSPENDED
    const [userId, setUserId] = useState("");
    const [ref1Id, setRef1Id] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [regFrom, setRegFrom] = useState(null);   // Date | null (registration_date >=)
    const [payFrom, setPayFrom] = useState(null);   // Date | null (premium_start_date >=)

    // ===== Пагинация =====
    const [page, setPage]   = useState(1);
    const [limit, setLimit] = useState(50);
    const [count, setCount] = useState(0);
    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(count / Math.max(1, limit))),
        [count, limit]
    );

    // ===== Данные/состояния =====
    const [users, setUsers]     = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // ===== Модалка редактирования =====
    const [selectedUser, setSelectedUser] = useState(null);
    const [modalShow, setModalShow]       = useState(false);
    const handleEditClick = useCallback((user) => {
        setSelectedUser(user);
        setModalShow(true);
    }, []);

    // ===== Вспомогательные =====
    const skipNextLoadRef = useRef(true);
    const toMinutes = (v) => {
        if (!v) return undefined;
        const d = v instanceof Date ? v : new Date(v);
        return Math.floor(d.getTime() / 60000);
    };

    // Собираем параметры для API
    const buildQuery = useCallback(() => {
        const base = {
            page,
            limit,
            q: q?.trim() || undefined,
            tariff_plan: tariffPlan || undefined,
            userId: userId || undefined,
            ref1Id: ref1Id || undefined,
            user_email: userEmail || undefined,
            registration_date: toMinutes(regFrom),
            pay_date: toMinutes(payFrom),
        };
        // выкидываем undefined
        return Object.fromEntries(Object.entries(base).filter(([, v]) => v !== undefined && v !== ""));
    }, [page, limit, q, tariffPlan, userId, ref1Id, userEmail, regFrom, payFrom]);

    // Загрузка
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const payload = await fetchUsersAdmin(buildQuery());
            const items = payload?.items || [];
            setUsers(items);
            setCount(Number(payload?.count || 0));
            setMessage(items.length ? "" : "No users found");
        } catch (e) {
            console.error("Users fetch failed:", e);
            setUsers([]);
            setCount(0);
            setMessage("Failed to fetch users");
            toast.error("Error fetching users", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    // Применить фильтр (на 1-ю страницу)
    const applyFilter = useCallback(async () => {
        setPage(1);
        setLoading(true);
        try {
            const payload = await fetchUsersAdmin({ ...buildQuery(), page: 1 });
            const items = payload?.items || [];
            setUsers(items);
            setCount(Number(payload?.count || 0));
            setMessage(items.length ? "" : "No users found");
        } catch (e) {
            console.error(e);
            toast.error("Failed to apply filter", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    // Reset — без запроса (как просили для BookSearch)
    const resetFilters = useCallback(() => {
        skipNextLoadRef.current = true;
        setQ("");
        setTariffPlan("");
        setUserId("");
        setRef1Id("");
        setUserEmail("");
        setRegFrom(null);
        setPayFrom(null);
        setPage(1);
        setUsers([]);
        setCount(0);
        setMessage("");
        setLoading(false);
    }, []);

    // Пагинация
    const handlePageChange = useCallback((next) => {
        const p = Math.max(1, Math.min(next, totalPages || 1));
        if (p !== page) setPage(p);
    }, [page, totalPages]);

    const handleLimitChange = useCallback((newLimit) => {
        const l = Math.max(1, Math.min(newLimit || 50, 200));
        if (l !== limit) {
            setLimit(l);
            setPage(1);
        }
    }, [limit]);

    // Автозагрузка при изменении page/limit
    useEffect(() => {
        if (skipNextLoadRef.current) {
            skipNextLoadRef.current = false;
            return;
        }
        loadData();
    }, [page, limit, loadData]);

    const handleModalClose = useCallback(async () => {
        setModalShow(false);
        setSelectedUser(null);

        // мгновенный рефетч текущей страницы с текущими фильтрами
        await loadData();
    }, [loadData]);


    // Функция форматирования даты + прошедшее время
    const formatDateWithElapsedTime = (minutes) => {
        if (!minutes) return "N/A";

        const date = new Date(minutes * 60000); // Преобразуем минуты в миллисекунды
        const now = new Date();

        // Форматируем дату в "12-Mar-2025"
        const formattedDate = format(date, "dd-MMM-yyyy", { locale: enUS });

        // Вычисляем разницу во времени
        const diffMinutes = differenceInMinutes(now, date);

        if (diffMinutes < 0) {
            return `${formattedDate} (Future)`;
        }

        const days = Math.floor(diffMinutes / 1440); // 1 день = 1440 минут
        const hours = Math.floor((diffMinutes % 1440) / 60);
        const minutesLeft = diffMinutes % 60;

        return `${formattedDate} (${days}D ${hours}H)`;
    };


    // Экспортируем из контроллера всё, что нужно рендеру
    // (Рендер вернём отдельно в следующем шаге)
    return (
        <>
            <Card className="shadow-sm bg-light p-0">
                <Card.Body>
                    <div className="d-flex flex-wrap gap-2 mb-3 align-items-end justify-content-start">
                        {/* Статус (как в оригинале) */}
                        <Dropdown onSelect={(value) => setTariffPlan(value || "")}>
                            <Dropdown.Toggle
                                variant="secondary"
                                size="sm"
                                style={{ minWidth: "120px" }}
                            >
                                {tariffPlan || "Status"}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item eventKey="FREE">FREE</Dropdown.Item>
                                <Dropdown.Item eventKey="ESSENTIAL">ESSENTIAL</Dropdown.Item>
                                <Dropdown.Item eventKey="PREMIUM">PREMIUM</Dropdown.Item>
                                <Dropdown.Item eventKey="MAXIMUM">MAXIMUM</Dropdown.Item>
                                <Dropdown.Item eventKey="SUSPENDED">SUSPENDED</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                        {/* Apply / Reset — как в оригинале */}
                        <Button variant="success" size="sm" onClick={applyFilter} disabled={loading}>
                            Apply Filter
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={resetFilters} disabled={loading}>
                            Reset
                        </Button>

                        {/* Новое поле поиска */}
                        <div style={{ width: 140 }} className="d-none d-md-block">
                        <Form.Control
                            type="text"
                            className="form-control-sm"
                            placeholder="Search…"
                            style={{ maxWidth: "160px" }}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                        </div>

                        {/* ID и Email — как в оригинале */}
                        <Form.Control
                            type="text"
                            className="form-control-sm"
                            placeholder="User ID"
                            style={{ maxWidth: "90px" }}
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                        />

                        <Form.Control
                            type="text"
                            className="form-control-sm"
                            placeholder="Ref1 ID"
                            style={{ maxWidth: '100px' }}
                            value={ref1Id}
                            onChange={(e) => setRef1Id(e.target.value)}
                        />

                        <Form.Control
                            type="text"
                            className="form-control-sm"
                            placeholder="User E-Mail"
                            style={{ maxWidth: "120px" }}
                            value={userEmail}
                            onChange={(e) => setUserEmail(e.target.value)}
                        />

                        {/* Даты — как в оригинале */}
                        <div className="d-flex align-items-center d-none d-md-block gap-2">
                            <small className="text-muted" style={{ whiteSpace: "nowrap" }}>
                                Registration:
                            </small>
                            <Form.Control
                                type="date"
                                size="sm"
                                value={regFrom ? new Date(regFrom).toISOString().slice(0, 10) : ""}
                                onChange={(e) => setRegFrom(e.target.value ? new Date(e.target.value) : null)}
                            />
                        </div>

                        <div className="d-flex align-items-center d-none d-md-block gap-2">
                            <small className="text-muted" style={{ whiteSpace: "nowrap" }}>
                                Pay Date:
                            </small>
                            <Form.Control
                                type="date"
                                size="sm"
                                value={payFrom ? new Date(payFrom).toISOString().slice(0, 10) : ""}
                                onChange={(e) => setPayFrom(e.target.value ? new Date(e.target.value) : null)}
                            />
                        </div>
                        {/* Per Page (PP) */}
                        <div className="d-flex align-items-end">
                            <div className="d-flex flex-column">
                                <div className="ms-3">
                                    <Form.Select
                                        size="sm"
                                        value={limit}
                                        onChange={(e) => handleLimitChange(Number(e.target.value))}
                                        style={{ width: 60 }}
                                    >
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={200}>200</option>
                                    </Form.Select>
                                </div>
                            </div>
                        </div>

                        <AdminNegativeBalanceReconcile onApplied={loadData} />
                        <AdminEmailMailerModal />

                    </div>

                    {/* Сообщение если нет пользователей */}
                    {message && <p className="text-muted mt-3 text-center">{message}</p>}
                    {loading ? (
                        <div className="d-flex align-items-center gap-2">
                            <Spinner size="sm" /> <small>Loading…</small>
                        </div>
                    ) : (<>
                    {/* Таблица (стили/колонки — как в оригинале) */}
                    {users.length > 0 && (
                        <div style={{ overflowX: "auto", whiteSpace: "nowrap", paddingBottom: "10px" }}>
                            <Table striped bordered hover size="sm" className="table-sm small" style={{ minWidth: "1200px" }}>
                                <thead className="small">
                                <tr className="small">
                                    <th>ID</th>
                                    <th>Username</th>
                                    <th>Email</th>
                                    <th>Amz Name</th>
                                    <th>Virt</th>
                                    <th>USD</th>
                                    <th>$AFF</th>
                                    <th>Plan</th>
                                    <th>CN</th>

                                    <th>Books</th>
                                    <th>Ass</th>
                                    <th>TAss</th>
                                    <th>Trust</th>
                                    <th>Register Date (Past)</th>
                                    <th>Pay Date (Past)</th>
                                    <th>Ref1</th>
                                    <th>RMod1</th>
                                    <th>Ref2</th>
                                    <th>RMod1</th>
                                    <th>Ref3</th>
                                    <th>RMod1</th>
                                </tr>
                                </thead>
                                <tbody>
                                {users.map((user) => (
                                    <tr
                                        className="align-start small"
                                        key={user?.id}
                                        onClick={() => handleEditClick(user)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td className="text-start py-0 px-1 small">{user?.id}</td>
                                        <td className="text-start py-0 px-1 small">{user?.username?.slice(0, 10)}</td>
                                        <td className="text-start py-0 px-1 small">{user?.email}</td>
                                        <td className="text-start py-0 px-1 small">{user?.amz_name?.slice(0, 20) || "N/A"} / <span className="fw-semibold">{user?.amz_country?.slice(18, 25)}</span> </td>
                                        <td className="text-start py-0 px-1 small">{user?.balance} Virt</td>
                                        <td className="text-start py-0 px-1 small">
                                            {Number.isFinite(Number(user?.lr_balance))
                                                ? `$${Number(user.lr_balance).toFixed(2)}`
                                                : "N/A"}
                                        </td>
                                        <td className="text-start py-0 px-1 small">
                                            {Number.isFinite(Number(user?.affiliate_balance))
                                                ? `$${Number(user.affiliate_balance).toFixed(2)}`
                                                : "N/A"}
                                        </td>

                                        <td className="text-start py-0 px-1 small">{user?.tariff_plan}</td>
                                        <td className="text-start py-0 px-1 small">{user?.amazon_rn_changes}</td>

                                        <td className="text-start py-0 px-1 small">{user?.bookCount ?? 0}</td>
                                        <td className="text-start py-0 px-1 small">{user?.assignmentCount ?? 0}</td>
                                        <td className="text-start py-0 px-1 small">{user?.takenAssignmentsCount ?? 0}</td>
                                        <td className="text-start py-0 px-1 small">{user?.level_of_trust}</td>
                                        <td className="text-start py-0 px-1 small">{formatDateWithElapsedTime(user?.registration_date)}</td>
                                        <td className="text-start py-0 px-1 small">{formatDateWithElapsedTime(user?.premium_start_date)}</td>
                                        <td className="text-start py-0 px-1 small">{user?.referrer_id}</td>
                                        <td className="text-start py-0 px-1 small">{user?.ref_affiliate_model_level1}</td>
                                        <td className="text-start py-0 px-1 small">{user?.referrer_l2_id}</td>
                                        <td className="text-start py-0 px-1 small">{user?.ref_affiliate_model_level2}</td>
                                        <td className="text-start py-0 px-1 small">{user?.referrer_l3_id}</td>
                                        <td className="text-start py-0 px-1 small">{user?.ref_affiliate_model_level3}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Пагинация внизу */}
                    {users.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-2">
                            <div className="small text-muted">
                                Total: {count} • Page {page} of {totalPages}
                            </div>
                            <Pagination size="sm" className="mb-0">
                                <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page <= 1} />
                                <Pagination.Item active>{page}</Pagination.Item>
                                <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} />
                            </Pagination>
                        </div>
                    )}
                    </>)}
                </Card.Body>
            </Card>

            {/* Модалка */}
            {selectedUser && (
                <EditUserAdmin
                    show={modalShow}
                    handleClose={handleModalClose}
                    userInfo={selectedUser}
                    onOpenUserBooks={onOpenUserBooks}
                    onOpenUserAuthorAssignments={onOpenUserAuthorAssignments}
                    onOpenUserReviewerAssignments={onOpenUserReviewerAssignments}
                />
            )}
        </>

    )
};

export default UserSearch;
